import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';

import { MessageRecipientSkeleton } from '../../Shared/skeletons';

const ignoredProps = ['isProperty'];

const StyledItemContainer = styled(Box, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isProperty }) => ({
  height: '5rem',
  width: '100%',
  borderBottom: `.1rem solid ${theme.palette.grey[300]}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  ...(isProperty && {
    backgroundColor: theme.palette.grey[200],
    cursor: 'default',
    fontWeight: theme.typography.fontWeightMedium,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '4.2rem',
  }),
}));

const GroupSubitem = ({ subitem, onSubitemClick, isSelected }) => {
  const { name } = subitem;
  return (
    <StyledItemContainer onClick={() => onSubitemClick(subitem)}>
      <Box display="flex" alignItems="center" height="100%">
        <Checkbox checked={isSelected} color="primary" size="medium" />
        <Box>{name}</Box>
      </Box>
    </StyledItemContainer>
  );
};

GroupSubitem.propTypes = {
  subitem: PropTypes.instanceOf(Object),
  onSubitemClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

const GroupItem = (props) => {
  const {
    group,
    groupKey,
    buildings,
    showSubitems,
    onSetShowSubitems,
    onAddGroup,
    checkedGroups,
  } = props;

  const isAllBuildings = Number(groupKey) === 0;
  const groupName = isAllBuildings
    ? group?.[0]?.name
    : buildings?.[groupKey]?.name;

  const onItemClick = () => {
    if (isAllBuildings)
      return onAddGroup({ ...group?.[0], isAllBuildings: true });
    onSetShowSubitems(groupKey);
    return null;
  };

  const onSubitemClick = (subitem) => {
    onAddGroup(subitem, buildings?.[groupKey]?.id);
  };

  const getIsSubitemSelected = (subitem) => {
    const { id } = subitem;
    return !!checkedGroups.find(
      ({ scope, building }) =>
        String(scope) === String(id) && String(building) === String(groupKey),
    );
  };

  return (
    <>
      <StyledItemContainer
        display="flex"
        alignItems="center"
        onClick={onItemClick}
      >
        <Box
          display="flex"
          alignItems="center"
          height="100%"
          justifyContent={isAllBuildings ? 'space-between' : 'start'}
        >
          <Box>{groupName}</Box>
          {isAllBuildings ? (
            <ChevronRightIcon />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              sx={showSubitems ? { transform: `rotate(0.5turn)` } : {}}
            >
              <ExpandMoreIcon />
            </Box>
          )}
        </Box>
      </StyledItemContainer>
      {showSubitems
        ? group.map((subitem) => (
            <GroupSubitem
              key={subitem?.name}
              subitem={subitem}
              onSubitemClick={onSubitemClick}
              isSelected={getIsSubitemSelected(subitem)}
            />
          ))
        : null}
    </>
  );
};

GroupItem.propTypes = {
  group: PropTypes.instanceOf(Array),
  checkedGroups: PropTypes.instanceOf(Array),
  groupKey: PropTypes.string,
  buildings: PropTypes.instanceOf(Object),
  showSubitems: PropTypes.bool,
  onSetShowSubitems: PropTypes.func,
  onAddGroup: PropTypes.func,
};

const RecipientItem = (props) => {
  const { recipient, index, checkedRecipients, roles, onAddRecipient } = props;
  // insert building name
  if (recipient.buildingDisplayName) {
    return (
      <StyledItemContainer
        key={`${recipient.buildingDisplayName}-${index}`}
        isProperty
      >
        <b>{recipient.buildingDisplayName}</b>
      </StyledItemContainer>
    );
  }

  const isItemSelected = checkedRecipients.includes(recipient.id);

  let displayedRole;
  let unit;

  // review
  if (!isEmpty(recipient.roles)) {
    const recipientRole = roles.filter(
      (role) => recipient.roles[0] === role.id,
    );
    displayedRole = get(recipientRole, '[0].displayName', '');
  } else if (!isEmpty(recipient.units)) {
    const recipientRole = roles.filter(
      (role) => recipient.units[0].role === role.id,
    );
    unit = get(recipient, 'units[0].name', '');
    displayedRole = get(recipientRole, '[0].displayName', '');
  } else if (!isEmpty(recipient.visitors)) {
    const recipientRole = roles.filter(
      (role) => recipient.visitors[0].role === role.id,
    );
    displayedRole = get(recipientRole, '[0].displayName', '');
  }

  const personName = `${recipient.firstName} ${recipient.lastName}`;

  return (
    <StyledItemContainer
      onClick={() => onAddRecipient(recipient)}
      key={`${recipient.id} ${index}`}
    >
      <Checkbox checked={isItemSelected} color="primary" size="medium" />

      <Box display="flex" width="100%" alignItems="center" gap="1rem">
        <Box width="40%" maxHeight="5rem" overflow="hidden">
          {personName}
        </Box>
        <Box width="35%">{displayedRole}</Box>
        <Box width="20%">{unit}</Box>
      </Box>
    </StyledItemContainer>
  );
};

RecipientItem.propTypes = {
  recipient: PropTypes.instanceOf(Object),
  index: PropTypes.number,
  checkedRecipients: PropTypes.instanceOf(Array),
  roles: PropTypes.instanceOf(Array),
  onAddRecipient: PropTypes.func,
};

const RecipientsComponent = ({
  recipients,
  groups,
  groupKeys,
  isGroups,
  loadNextRecipients,
  nextRecipients,
  onAddRecipient,
  onAddGroup,
  checkedRecipients,
  checkedGroups,
  roles,
  buildings,
  insertBuildingName,
  height,
}) => {
  const [showSubitems, setShowSubitems] = useState({});

  const onSetShowSubitems = (groupKey) =>
    setShowSubitems({
      ...showSubitems,
      [groupKey]: !showSubitems[groupKey],
    });

  if (isGroups) {
    return (
      <Box height={height}>
        <InfiniteScroll
          dataLength={1000}
          next={() => {}}
          hasMore={false}
          height={height}
          loader={<MessageRecipientSkeleton />}
        >
          {groupKeys.map((groupKey, index) => (
            <GroupItem
              key={groupKey}
              groupKey={groupKey}
              group={groups[groupKey]}
              index={index}
              checkedGroups={checkedGroups}
              onAddGroup={onAddGroup}
              buildings={buildings}
              showSubitems={showSubitems[groupKey]}
              onSetShowSubitems={onSetShowSubitems}
            />
          ))}
        </InfiniteScroll>
      </Box>
    );
  }

  const recipientsWithBuildingName = insertBuildingName(recipients);

  return (
    <Box height={height}>
      <InfiniteScroll
        dataLength={recipients?.length}
        next={() => loadNextRecipients()}
        hasMore={nextRecipients}
        height={height}
        loader={<MessageRecipientSkeleton />}
      >
        {recipientsWithBuildingName?.map((recipient, index) => (
          <RecipientItem
            key={recipient?.id || recipient?.buildingDisplayName}
            recipient={recipient}
            index={index}
            checkedRecipients={checkedRecipients}
            roles={roles}
            onAddRecipient={onAddRecipient}
          />
        ))}
      </InfiniteScroll>
    </Box>
  );
};

RecipientsComponent.defaultProps = {
  nextRecipients: null,
};
RecipientsComponent.propTypes = {
  isGroups: PropTypes.bool.isRequired,
  loadNextRecipients: PropTypes.func.isRequired,
  nextRecipients: PropTypes.instanceOf(Object),
  buildings: PropTypes.instanceOf(Object),
  onAddRecipient: PropTypes.func.isRequired,
  onAddGroup: PropTypes.func.isRequired,
  insertBuildingName: PropTypes.func.isRequired,
  checkedRecipients: PropTypes.instanceOf(Array).isRequired,
  roles: PropTypes.instanceOf(Array).isRequired,
  recipients: PropTypes.instanceOf(Array),
  groups: PropTypes.instanceOf(Object),
  groupKeys: PropTypes.instanceOf(Array),
  checkedGroups: PropTypes.instanceOf(Array),
  height: PropTypes.number,
};

export default RecipientsComponent;
